import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { gradeChipText, type CuratorGrade } from '@/lib/lessonFeedback';
import { cn } from '@/lib/utils';

/**
 * Чип «90/100» и стрелка у урока в списке ученика (D11, 10.09.2026).
 *
 * Нажатие на чип или на стрелку раскрывает под строкой фидбек куратора.
 * Нет оценки — нет ни чипа, ни стрелки.
 *
 * ⚠️ КНОПКИ СТОЯТ РЯДОМ СО ССЫЛКОЙ НА УРОК, А НЕ ВНУТРИ НЕЁ. Строка урока —
 * ссылка; кнопка внутри неё уводила бы в урок вместо раскрытия. Нажатие всё
 * равно глушится (`preventDefault` + `stopPropagation`) — на случай, если
 * компонент когда-нибудь окажется внутри ссылки.
 *
 * ⚠️ ЗОНА НАЖАТИЯ 44px на телефоне при маленьком визуальном чипе: сама плашка
 * мелкая, а кнопка вокруг неё — во весь палец.
 */
export function LessonGradeChip({
  curatorGrade,
  expanded,
  onToggle,
  className,
}: {
  curatorGrade: CuratorGrade | null | undefined;
  expanded: boolean;
  onToggle: () => void;
  className?: string;
}) {
  const { t } = useTranslation();
  const text = gradeChipText(curatorGrade);
  if (!text) return null;

  const toggle = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onToggle();
  };

  return (
    <span className={cn('flex flex-none items-center', className)}>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-label={t('lessons.gradeChipLabel', { grade: curatorGrade?.grade ?? 0 })}
        className="grid min-h-11 place-items-center px-1 md:min-h-9"
      >
        <span className="rounded-full bg-info-container px-2.5 py-[3px] text-[12px] font-bold tabular-nums text-info">
          {text}
        </span>
      </button>
      <button
        type="button"
        onClick={toggle}
        aria-expanded={expanded}
        aria-label={expanded ? t('lessons.hideFeedback') : t('lessons.showFeedback')}
        className="grid h-11 w-11 place-items-center rounded-full text-on-surface-variant transition hover:bg-surface-container-high md:h-9 md:w-9"
      >
        <span
          className={cn('material-symbols-outlined text-[20px] transition-transform', expanded && 'rotate-180')}
          aria-hidden="true"
        >
          expand_more
        </span>
      </button>
    </span>
  );
}

/** Раскрытый фидбек куратора под строкой урока. */
export function LessonFeedbackPanel({
  curatorGrade,
  className,
}: {
  curatorGrade: CuratorGrade | null | undefined;
  className?: string;
}) {
  const { t } = useTranslation();
  if (!curatorGrade) return null;
  return (
    <div className={cn('rounded-[14px] bg-surface-container-lowest px-3.5 py-3', className)}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-on-surface-variant">
        {t('lessons.curatorFeedback')}
      </p>
      <p className="mt-1 whitespace-pre-wrap text-[13.5px] leading-relaxed text-on-surface">
        {curatorGrade.feedback ?? t('lessons.noFeedback')}
      </p>
    </div>
  );
}
